import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import { api, type SearchEntity, type SearchItem, type SearchOut } from "@/api/client";
import { Highlighted } from "@/components/Highlighted";
import { useLiveSearch } from "@/lib/useLiveSearch";
import "./globalSearch.css";

/**
 * One box over everything: people, resumes, vacancies, clients, documents,
 * recruiter notes and message bodies.
 *
 * newats never had this — the magnifier in the sidebar had no `onClick` at all,
 * so the only way to find anything was to guess which page it lived on and use
 * that page's own box, each of which searched two or three fields.
 */

const ROUTE: Record<SearchEntity, (item: SearchItem) => string | null> = {
  person: (i) => `/candidates?person=${i.id}`,
  resume: (i) => `/candidates?resume=${i.id}`,
  vacancy: (i) => `/vacancies/${(i.extra.number as number | undefined) ?? i.id}`,
  company: (i) => `/clients?company=${i.id}`,
  document: () => "/documents",
  // A note or a message is worth opening at the person it belongs to; without
  // one there is nothing useful to navigate to, so the row is not clickable.
  note: (i) => (i.extra.person_id ? `/candidates?person=${i.extra.person_id}` : null),
  message: (i) => (i.extra.person_id ? `/chats?person=${i.extra.person_id}` : null),
};

const REASON_LABEL: Record<string, string> = {
  phone: "по телефону",
  email: "по почте",
  handle: "по нику",
  name: "по имени",
  title: "по названию",
  skill: "по навыку",
  experience: "по опыту",
  education: "по образованию",
  area: "по городу",
  company: "по компании",
  number: "по номеру",
  text: "по тексту",
};

export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, loading, error, stale } = useLiveSearch<SearchOut>(
    query,
    (q, signal) => api.search(q, { signal, limit: 5 }),
    { minLength: 2, delayMs: 200, enabled: open, allowEmpty: false },
  );

  // Flatten the groups once so arrow keys can walk the whole list rather than
  // group by group.
  const flat = useMemo(() => {
    const out: { item: SearchItem; entity: SearchEntity }[] = [];
    for (const group of data?.groups ?? []) {
      for (const item of group.items) out.push({ item, entity: group.entity });
    }
    return out;
  }, [data]);

  useEffect(() => setCursor(0), [data]);

  useEffect(() => {
    if (open) {
      setQuery("");
      // The autofocus has to wait for the dialog to be in the DOM.
      const t = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  const go = useCallback(
    (entry: { item: SearchItem; entity: SearchEntity }) => {
      const path = ROUTE[entry.entity](entry.item);
      if (!path) return;
      onClose();
      navigate(path);
    },
    [navigate, onClose],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter" && flat[cursor]) {
      e.preventDefault();
      go(flat[cursor]);
    }
  };

  if (!open) return null;

  let index = -1;
  return (
    <div className="gs-backdrop" onMouseDown={onClose} role="presentation">
      <div
        className="gs-panel"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label="Поиск по всему"
      >
        <div className="gs-input-row">
          <Icon name="search" size={17} className="gs-input-icon" />
          <input
            ref={inputRef}
            className="gs-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Имя, телефон, почта, ник, компания, навык, номер вакансии…"
            aria-label="Поисковый запрос"
          />
          {stale && <span className="gs-spinner" aria-label="Идёт поиск" />}
          <kbd className="gs-kbd">esc</kbd>
        </div>

        <div className="gs-results">
          {query.trim().length === 1 && (
            <div className="gs-hint">Введите ещё хотя бы один символ</div>
          )}
          {error && <div className="gs-hint gs-hint--error">{error}</div>}
          {data?.partial && (
            // Being explicit beats a silently short list: the old search
            // truncated without a word and looked simply wrong.
            <div className="gs-hint gs-hint--warn">
              Показана часть результатов — поиск не успел за отведённое время
            </div>
          )}
          {data && !loading && flat.length === 0 && query.trim().length >= 2 && (
            <div className="gs-hint">Ничего не найдено</div>
          )}

          {(data?.groups ?? []).map((group) => (
            <div className="gs-group" key={group.entity}>
              <div className="gs-group-title">
                {group.label}
                {group.total != null && group.total > group.items.length && (
                  <span className="gs-group-count">{group.total}</span>
                )}
              </div>
              {group.items.map((item) => {
                index += 1;
                const active = index === cursor;
                const at = index;
                return (
                  <button
                    key={`${group.entity}:${item.id}`}
                    type="button"
                    className={"gs-row" + (active ? " gs-row--active" : "")}
                    onMouseEnter={() => setCursor(at)}
                    onClick={() => go({ item, entity: group.entity })}
                  >
                    {item.avatar_url ? (
                      <img className="gs-avatar" src={item.avatar_url} alt="" />
                    ) : (
                      <span className="gs-avatar gs-avatar--blank" />
                    )}
                    <span className="gs-row-text">
                      <span className="gs-row-title">{item.title}</span>
                      {item.snippet ? (
                        // The excerpt is the only way a body-text hit can show
                        // *why* it matched. Its text is raw candidate input, so
                        // it is rendered as text nodes — never as HTML.
                        <span className="gs-row-sub">
                          <Highlighted snippet={item.snippet} />
                        </span>
                      ) : (
                        item.subtitle && <span className="gs-row-sub">{item.subtitle}</span>
                      )}
                    </span>
                    {item.reasons[0] && REASON_LABEL[item.reasons[0]] && (
                      <span className="gs-row-reason">{REASON_LABEL[item.reasons[0]]}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {data?.backend && (
          <div className="gs-foot">
            {data.took_ms} мс · {data.backend === "elasticsearch" ? "Elasticsearch" : "Postgres"}
          </div>
        )}
      </div>
    </div>
  );
}

/** Ctrl/Cmd+K anywhere, and Escape to close. */
export function useGlobalSearchHotkey(onOpen: () => void) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // `e.code` is the physical key. `e.key` follows the layout, and on the
      // Russian one it is "л" — the handler never fired, nothing called
      // preventDefault, and Chrome took Ctrl+K for its own address-bar search.
      if ((e.ctrlKey || e.metaKey) && (e.code === "KeyK" || e.key.toLowerCase() === "k")) {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpen]);
}
